import {isFeatureEnabled} from 'feature-flags';
import {closeCurrentDialog, openDialog, showNotification} from 'platform/components';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetVehicleMileageQuery, usePostVehicleMileageMutation} from '@dms/api/sales';
import featureFlags from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {ApiError, Nullish} from 'shared/web';

import {handleApiError} from '../../../utils/handleApiError';
import {
  RecordVehicleMileageFormProps,
  RecordVehicleMileageForm,
} from '../components/RecordVehicleMileageForm';

type HandleUpdateOdometerProps = Omit<RecordVehicleMileageFormProps, 'vehicleId'>;
export type UpdateOdometerStatus = 'completed' | 'cancelled' | 'failed';

export const useUpdateOdometer = (vehicleId: string | Nullish) => {
  const [handleRecordMilage] = usePostVehicleMileageMutation();
  const {data: vehicleMileage} = useGetVehicleMileageQuery(
    {vehicleId: vehicleId ?? ''},
    {skip: isNilOrEmpty(vehicleId)}
  );

  const updateOdometer = async (args: HandleUpdateOdometerProps): Promise<UpdateOdometerStatus> => {
    if (!isFeatureEnabled(featureFlags.VEHICLE_MILEAGE_HISTORY)) {
      return 'completed';
    }

    if (isNil(vehicleId)) {
      showNotification.error();
      return 'failed';
    }

    if (args.milage >= (vehicleMileage?.mileage ?? 0)) {
      return await handleRecordMilage({
        vehicleId,
        writeMileageRequestBody: {
          id: args.recordId,
          mileage: args.milage,
          action: args.action,
          title: args.recordTitle,
          note: null,
        },
      })
        .unwrap()
        .then(() => {
          closeCurrentDialog();
          return 'completed' as const;
        })
        .catch((error: ApiError) => {
          handleApiError(error);
          return 'failed' as const;
        });
    }

    return await new Promise<UpdateOdometerStatus>((resolve) => {
      openDialog(
        <RecordVehicleMileageForm
          {...args}
          vehicleId={vehicleId}
          onCompleted={() => resolve('completed')}
          onCancelled={() => resolve('cancelled')}
        />,
        {
          title: i18n.t('entity.vehicle.labels.milageSubmit'),
          size: 'small',
        }
      );
    });
  };

  return [updateOdometer];
};
